import { AccountingPeriodStatus } from '../stock/stockModels'
import guid, { Guid } from './../../infrastructure/guid'

export type StringDate = string

export type DutyStatus = string

export type MktSaleForecast = {
    date: StringDate | null
    daysInPeriod: number | null
    company: string | null
    values: MktSaleForecastValue[] | null
    snapshotsInfos: MktSaleForecastSnapshotInfo[] | null
}

export type ForecastByCompanyAndMonth = {
    date: StringDate
    endDate: StringDate | null
    company: string
}

export type MktSaleForecastSnapshotInfo = {
    id: Guid
    type: string
    company: string
    createdAt: StringDate
    start: string
    end: string
}

export type SupplyProduct = {
    id: Guid
    code: string
    unit: string
    productGroup: string | null
    conversionFactor: number
    defaultQuotationCode: string
}

export type CustomerSegment = {
    code: string
    site: string
    company: string
    dutyStatus: DutyStatus
    products: CustomerSegmentProduct[]
}

export type CustomerSegmentProduct = {
    id: string
    code: string
    unit: string
    hasStock: boolean
    periodCoeff: number
}

export type Site = {
    code: string
    name: string
    siteGroup: string
    isSellingPoint: boolean
    dutyStatuses: DutyStatus[],
    allocatedQuantity: string | null
    unusableQuantity: string | null
}

export type SnapshotType = 'Initial' | 'LongTermPlan'

export type MktSaleForecastValue = {
    perimeter: MktSaleForecastValuePerimeter
    volume: number | null
}

export type MktSaleForecastValuePerimeter = {
    channel: string | null
    productId: Guid | null
    customerSegment: string | null
    site: string | null
}

export type Company = {
    code: string
    name: string
    dutyStatuses: DutyStatus[]
    customerSegments: string[]
    country: string
}

export type ChannelCustomerSegment = {
    company: string
    channel: string
    customerSegment: string
    dutyStatus: string
}

export type MktSales = {
    movementItems: MktSaleMovement[]
    chart: MktSaleChart
    sum: MktSalesSum
    truncated: boolean
}

export type MktSalesSum = {
    longTermPlanVolume: number
    initialVolume: number
    forecastQuantity: number
    plannedQuantity: number
    siteQuantity: number
    sapQuantity: number
    quantity: number
}

export type MktSaleMovement = {
    id: Guid
    date: StringDate
    customerSegment: string | null
    initialVolume: number | null
    longTermPlanVolume: number | null
    forecastQuantity: number | null
    plannedQuantity: number | null
    siteQuantity: number | null
    accountingPeriodStatus: AccountingPeriodStatus | null
    sapQuantity: number | null
    quantity: number | null
    status: MovementStatus
    productId: string
    company: string
    site: string
    hasDemandPlanChange: boolean
    percentage: number | null
}

export type MktSaleChart = {
    forecasts: ChartPoint[]
    initialForecasts: ChartPoint[]
    longTermPlanForecasts: ChartPoint[]
    actualSales: ChartPoint[]
    sites: ChartPoint[]
    saps: ChartPoint[]
}

export let emptyChart: MktSaleChart = {
    forecasts: [],
    initialForecasts: [],
    longTermPlanForecasts: [],
    actualSales: [],
    sites: [],
    saps: [],
}

export type ChartPoint = {
    date: Date
    volume: number
}

export enum MovementStatus {
    Forecast = 0,
    Planned = 1,
    Actual = 2
}

export type MarketSalesFilters = {
    companies?: string[] | null
    site?: string[] | null
    productId?: Guid[] | null
    customerSegments?: string[] | null
    startDate?: StringDate | null
    endDate?: StringDate | null
    useSiteInPercentageCompute?: boolean
}

export type MktSaleDate = {
    date: string,
    isOpen: boolean,
    quantity: number | null,
    initialVolume: number | null,
    longTermPlanVolume: number | null,
    forecastQuantity: number | null,
    plannedQuantity: number | null,
    siteQuantity: number | null,
    accountingPeriodStatus: AccountingPeriodStatus | null,
    sapQuantity: number | null,
    status: MovementStatus,
    percentage: number | null,
    movements: MktSaleMovement[]
}

export type MktSaleShipTosFilters = {
    companys: string[] | null
    sites: string[] | null
    date: string | null
    productIds: Guid[] | null
    customerSegments: string[] | null
}

export type MktSaleForecastShipToDetails = {
    id: Guid
    date: Date
    company: string
    companyName: string
    productId: Guid
    product: string
    customerSegment: string
    dutyStatus: string
    site: string
    shipTo: string
    forecastQuantity: number
    plannedQuantity: number
    status: MovementStatus
    dealReferences: { [dealId: string]: string }
    initialVolume: number | null
    longTermPlanVolume: number | null
    quantity: number | null
    siteQuantity: number | null
    sapQuantity: number | null
    shipToName: string | null
    soldTo: string | null
    soldToName: string | null
    destination: string | null
    flattenedDealReferences: string
}
export type MktSaleForecastShipToDetailsQuantityProperties = Pick<MktSaleForecastShipToDetails,
    'initialVolume' | 'longTermPlanVolume' | 'forecastQuantity' | 'plannedQuantity' | 'quantity' | 'siteQuantity' | 'sapQuantity'>

export type ExportMktSalesArgs = {
    filename: string,
    startDate?: string | null,
    endDate?: string | null,
    site?: string[] | null
    productId?: Guid[] | null
    companies?: string[] | null
    customerSegments?: string[] | null
}

export type ShipToDetailsFilters = {
    companyName: string | null
    product: string | null
    customerSegment: string | null
    site: string | null
    shipTo: string | null
    shipToName: string | null
    soldTo: string | null
    soldToName: string | null
    destination: string | null
    dealRef: string | null
    flattenedDealReferences: string | null
}

export let defaultShipToDetailsFilters: ShipToDetailsFilters = {
    companyName: null,
    product: null,
    customerSegment: null,
    site: null,
    shipTo: null,
    shipToName: null,
    soldTo: null,
    soldToName: null,
    destination: null,
    dealRef: null,
    flattenedDealReferences: null
}

export type ForecastSnapshotParam = {
    company: string
    start: string
    end: string
    type: SnapshotType
}

export type MktSalesDemandPlanChange = {
    dateFrom: StringDate,
    dateTo: StringDate,
    days: number,
    weightedDays: number,
    productId: Guid,
    site: string,
    company: string | null
    customerSegment?: string | null,
    takeForecastValue: boolean,
    newPlannedVolumePerDay: number,
    newTotalPlannedVolume: number,
    oldTotalPlannedVolume: number,
    percentagePlannedVolume: number,
    originalPlannedVolumePerDay: number,
    comment: string,
    id: Guid
}

export let defaultDemandPlanChange = (): MktSalesDemandPlanChange => ({
    dateFrom: '',
    dateTo: '',
    days: 0,
    weightedDays: 0,
    productId: guid.empty,
    site: '',
    company: null,
    customerSegment: null,
    takeForecastValue: true,
    newPlannedVolumePerDay: 0,
    newTotalPlannedVolume: 0,
    oldTotalPlannedVolume: 0,
    percentagePlannedVolume: 0,
    originalPlannedVolumePerDay: 0,
    comment: '',
    id: guid.empty
})